import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Download, Eye, Users, CreditCard, QrCode } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => (v === null || v === undefined ? "—" : Number(v).toFixed(2));

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copiado`);
  } catch (_) {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success(`${label} copiado`);
    } catch (__) {
      toast.error("No se pudo copiar");
    }
  }
};

const STATUS_ES = { pending: "pendiente", paid: "pagada", reversal: "reversión" };

// CORTE 5-B — Vista pública del partner: solo lectura, sin login, CERO datos personales.
export default function PartnerView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [kit, setKit] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/partner/view/${token}`),
          axios.get(`${API}/partner/view/${token}/kit`),
        ]);
        setData(r1.data);
        setKit(r2.data);
      } catch (_) {
        setNotFound(true);
      }
    })();
  }, [token]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
        <div className="text-center" data-testid="partner-view-invalid">
          <h1 className="text-2xl font-bold text-white mb-2">Enlace no válido</h1>
          <p className="text-slate-400">Este enlace fue revocado o no existe. Contacta al equipo AvisoCar.</p>
        </div>
      </div>
    );
  }

  if (!data || !kit) {
    return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-400">Cargando…</div>;
  }

  const f = data.funnel;

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 sm:p-8" data-testid="partner-view-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <header>
          <p className="text-amber-400 text-xs uppercase tracking-widest mb-1">Panel de partner · AvisoCar</p>
          <h1 className="text-3xl font-bold" data-testid="partner-view-name">{data.partner_name}</h1>
          <p className="text-slate-400 text-sm mt-1">
            Código <span className="font-mono text-amber-300" data-testid="partner-view-code">{data.code}</span> · Comisión {data.commission_pct}% sobre el monto pagado
          </p>
        </header>

        {/* Embudo — TRES números distintos */}
        <section className="grid grid-cols-3 gap-3">
          {[
            { icon: Eye, label: "Visitas", value: f.visits, testid: "funnel-visits" },
            { icon: Users, label: "Registros", value: f.registrations, testid: "funnel-registrations" },
            { icon: CreditCard, label: "Pagos confirmados", value: f.confirmed_payments, testid: "funnel-payments" },
          ].map(({ icon: Icon, label, value, testid }) => (
            <div key={label} className="bg-slate-900 border border-slate-800 rounded-2xl p-4 text-center">
              <Icon className="w-5 h-5 mx-auto text-amber-400 mb-1" />
              <p className="text-3xl font-bold" data-testid={testid}>{value}</p>
              <p className="text-[11px] text-slate-400 mt-1">{label}</p>
            </div>
          ))}
        </section>
        <p className="text-xs text-slate-500 -mt-3" data-testid="funnel-legend">{data.funnel_legend}</p>

        {/* Subtotales POR MONEDA */}
        <section className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <h2 className="text-lg font-semibold mb-3">Tu comisión (por moneda, nunca sumada)</h2>
          {Object.keys(data.subtotals_by_currency).length === 0 ? (
            <p className="text-slate-500 text-sm" data-testid="subtotals-empty">Todavía no hay ventas confirmadas con tu código.</p>
          ) : (
            <div className="space-y-2" data-testid="subtotals-list">
              {Object.entries(data.subtotals_by_currency).map(([cur, t]) => (
                <div key={cur} className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm bg-slate-950/60 rounded-xl p-3" data-testid={`subtotal-${cur}`}>
                  <div><p className="text-slate-500 text-[11px]">Ganada ({cur})</p><p className="font-bold text-amber-300">{fmt(t.commission_earned)}</p></div>
                  <div><p className="text-slate-500 text-[11px]">Pendiente</p><p className={`font-bold ${t.commission_pending < 0 ? "text-rose-300" : "text-sky-300"}`}>{fmt(t.commission_pending)}</p>{t.commission_pending < 0 && <p className="text-[9px] text-rose-400/70">reversión a compensar</p>}</div>
                  <div><p className="text-slate-500 text-[11px]">Pagada</p><p className="font-bold text-emerald-300">{fmt(t.commission_paid)}</p></div>
                  <div><p className="text-slate-500 text-[11px]">Ventas ({cur})</p><p className="font-bold">{fmt(t.sales_amount)}</p></div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Detalle por venta */}
        <section className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">Detalle por venta</h2>
            <a href={`${API}/partner/view/${token}/export.csv`} data-testid="partner-csv-btn"
               className="inline-flex items-center gap-1.5 text-xs bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-full px-3 py-1.5">
              <Download className="w-3.5 h-3.5" /> Descargar CSV
            </a>
          </div>
          {data.sales.length === 0 ? (
            <p className="text-slate-500 text-sm" data-testid="sales-empty">Sin movimientos todavía.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="sales-table">
                <thead>
                  <tr className="text-slate-500 text-left border-b border-slate-800">
                    <th className="py-2 pr-2">tx_id</th><th className="pr-2">Plan</th><th className="pr-2">Monto</th>
                    <th className="pr-2">%</th><th className="pr-2">Comisión</th><th className="pr-2">Estado</th><th>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sales.map((s, i) => (
                    <tr key={i} className="border-b border-slate-800/50" data-testid={`sale-row-${s.tx_id || i}`}>
                      <td className="py-2 pr-2 font-mono text-amber-300">{s.tx_id || "—"}</td>
                      <td className="pr-2">{s.plan}</td>
                      <td className="pr-2">{fmt(s.amount)} {s.currency}</td>
                      <td className="pr-2">{s.pct != null ? `${s.pct}%` : "—"}</td>
                      <td className="pr-2 font-semibold">{fmt(s.commission)} {s.currency}</td>
                      <td className="pr-2">
                        <span className={s.status === "paid" ? "text-emerald-300" : s.status === "reversal" ? "text-rose-300" : "text-sky-300"}>
                          {STATUS_ES[s.status] || s.status}
                        </span>
                        {s.paid_at && <span className="text-slate-500"> · {s.paid_at.slice(0, 10)}</span>}
                      </td>
                      <td className="text-slate-400">{(s.date || "").slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Kit para compartir */}
        <section className="bg-slate-900 border border-amber-500/30 rounded-2xl p-5" data-testid="partner-kit">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><QrCode className="w-5 h-5 text-amber-400" /> Kit para compartir</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <p className="text-[11px] text-slate-500 mb-1">Tu código</p>
                <div className="flex items-center gap-2">
                  <code className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 font-mono text-amber-300 flex-1">{kit.code}</code>
                  <button onClick={() => copyText(kit.code, "Código")} className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg" data-testid="kit-copy-code"><Copy className="w-4 h-4" /></button>
                </div>
              </div>
              <div>
                <p className="text-[11px] text-slate-500 mb-1">Tu link de referido</p>
                <div className="flex items-center gap-2">
                  <code className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 font-mono text-xs flex-1 truncate">{kit.ref_link}</code>
                  <button onClick={() => copyText(kit.ref_link, "Link")} className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg" data-testid="kit-copy-link"><Copy className="w-4 h-4" /></button>
                </div>
              </div>
              <div>
                <p className="text-[11px] text-slate-500 mb-1">Texto para tu bio</p>
                <div className="bg-slate-950 border border-slate-700 rounded-lg p-3 text-xs text-slate-300">{kit.bio_text}</div>
                <button onClick={() => copyText(kit.bio_text, "Texto de bio")} className="mt-1.5 text-xs text-amber-300 hover:underline" data-testid="kit-copy-bio">Copiar texto de bio</button>
              </div>
              <div>
                <p className="text-[11px] text-slate-500 mb-1">Texto para story</p>
                <div className="bg-slate-950 border border-slate-700 rounded-lg p-3 text-xs text-slate-300 whitespace-pre-line">{kit.story_text}</div>
                <button onClick={() => copyText(kit.story_text, "Texto de story")} className="mt-1.5 text-xs text-amber-300 hover:underline" data-testid="kit-copy-story">Copiar texto de story</button>
              </div>
            </div>
            <div className="flex flex-col items-center justify-center gap-3">
              <img src={`data:image/png;base64,${kit.qr_png_base64}`} alt="QR de tu link de referido" className="w-56 h-56 bg-white rounded-xl p-2" data-testid="kit-qr-img" />
              <a href={`data:image/png;base64,${kit.qr_png_base64}`} download={`avisocar_ref_${kit.code}.png`}
                 className="inline-flex items-center gap-1.5 text-xs bg-amber-600 hover:bg-amber-500 rounded-full px-4 py-2 font-semibold" data-testid="kit-qr-download">
                <Download className="w-3.5 h-3.5" /> Descargar QR (alta resolución)
              </a>
              <p className="text-[10px] text-slate-500 text-center">Apto para imprimir. Al escanearlo abre tu link con el descuento preaplicado.</p>
            </div>
          </div>
        </section>

        <p className="text-[11px] text-slate-600 text-center pb-6">
          Vista de solo lectura · Sin datos personales de compradores · Actualizada {new Date(data.generated_at).toLocaleString("es-PE")}
        </p>
      </div>
    </div>
  );
}
